import { t } from 'i18next';
import { upperFirst } from 'lodash';
import { Control, FieldError, FieldErrors, FieldValues, UseFormWatch } from 'react-hook-form';

import { FormField } from 'core/components';
import { NON_EMPTY } from 'core/constants';
import { AllowedDecimalsTooltip } from 'core/elements';
import { InputType } from 'core/enums';
import { CorporateLocationFormField, ShipmentTypeFormField } from 'corporate/elements';
import { CorporateLocationType } from 'corporate/enums';
import { ManufacturerFormField } from 'parts/elements';
import { PricingHelper } from 'pricing/helpers';
import {
  PriceBasedOnBulkField,
  PricingDateFormField,
  PricingStrategyFormField,
} from 'quotes/elements';
import { ICustomerQuotePart } from 'quotes/types';
import { getCostInputProps, getPurchaseCostLabel } from 'quotes/utils';
import { UpsertTaxonomyGroupFormFields } from 'taxonomy/components';
import { Size } from 'theme/enums';

import { QUOTE_PART_UPDATE_BULK_FIELD_ORDER, QuotePartUpdateBulkField } from './enums';

export const getFieldOptions = () =>
  QUOTE_PART_UPDATE_BULK_FIELD_ORDER.map((field) => ({
    label: t(`label${upperFirst(field)}`),
    value: field,
  }));

export const getFormField = (
  value: QuotePartUpdateBulkField,
  control: Control<FieldValues>,
  errors: FieldErrors<FieldValues>,
  isSelected = false,
  watch: UseFormWatch<FieldValues>,
  uoms = [],
  isAlternateVersionEnabled = false,
  selectedQuoteParts: ICustomerQuotePart[] = [],
  costDecimals: number = PricingHelper.DEFAULT_COST_DECIMALS
) => {
  if (!isSelected) {
    return;
  }

  const editVendorQuoteNumberValue = watch('editVendorQuoteNumber');

  switch (value) {
    case QuotePartUpdateBulkField.UOM:
      return (
        <FormField
          name="uom"
          type={InputType.SELECT}
          inputProps={{
            control,
            error: errors?.uom as unknown as FieldError,
            placeholder: t('placeholderUpsertLineUom'),
            options: uoms,
            getLabel: (option) => option?.name,
            getValue: (option) => option?.name,
          }}
        />
      );
    case QuotePartUpdateBulkField.QUANTITY:
      return (
        <FormField
          name="quantity"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            control,
            error: errors?.quantity as unknown as FieldError,
            placeholder: t('placeholderUpsertLineQuantity'),
            slotProps: {
              input: {
                min: 0,
              },
            },
          }}
        />
      );
    case QuotePartUpdateBulkField.PURCHASE_COST: {
      return (
        <FormField
          name="purchaseCost"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            control,
            error: errors?.purchaseCost as unknown as FieldError,
            placeholder: getPurchaseCostLabel(isAlternateVersionEnabled),
            endDecorator: <AllowedDecimalsTooltip decimals={costDecimals} />,
            slotProps: {
              input: {
                prefix: '$',
                thousandSeparator: true,
                decimalScale: costDecimals,
                fixedDecimalScale: true,
              },
            },
          }}
        />
      );
    }
    case QuotePartUpdateBulkField.VENDOR_QUOTE_NUMBER:
      return (
        <FormField
          name="vendorQuoteNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{
            placeholder: t('labelVendorQuoteNumber'),
            control,
            error: errors?.vendorQuoteNumber,
            required: !!editVendorQuoteNumberValue,
          }}
        />
      );
    case QuotePartUpdateBulkField.REPLACEMENT_COST: {
      return (
        <FormField
          name="replacementCost"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            control,
            error: errors?.replacementCost as unknown as FieldError,
            placeholder: t('labelReplacementCost'),
            ...getCostInputProps(costDecimals),
          }}
        />
      );
    }
    case QuotePartUpdateBulkField.REPLACEMENT_COST_NUMBER:
      return (
        <FormField
          name="replacementCostNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{
            control,
            error: errors?.replacementCostNumber,
            placeholder: t('labelReplacementCostNumber'),
          }}
        />
      );
    case QuotePartUpdateBulkField.PRICE:
      return (
        <FormField
          name="price"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            control,
            error: errors?.price as unknown as FieldError,
            placeholder: t('placeholderUpsertLinePrice'),
            endDecorator: <AllowedDecimalsTooltip decimals={PricingHelper.DEFAULT_MAX_DECIMALS} />,
            slotProps: {
              input: {
                prefix: '$',
                thousandSeparator: true,
                decimalScale: PricingHelper.DEFAULT_MAX_DECIMALS,
              },
            },
          }}
        />
      );
    case QuotePartUpdateBulkField.MARGIN:
      return (
        <FormField
          name="margin"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            control,
            error: errors?.margin as unknown as FieldError,
            placeholder: t('placeholderUpsertLineMargin'),
            slotProps: {
              input: {
                max: 100,
                decimalScale: 2,
                allowNegative: true,
                suffix: '%',
              },
            },
          }}
        />
      );
    case QuotePartUpdateBulkField.PRICE_BASED_ON:
      return (
        <PriceBasedOnBulkField
          selectedQuoteParts={selectedQuoteParts}
          sx={{ ml: '30px !important' }}
        />
      );
    case QuotePartUpdateBulkField.PRICE_DECIMALS:
      return (
        <FormField
          name="priceDecimals"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            control,
            error: errors?.priceDecimals as unknown as FieldError,
            placeholder: t('placeholderUpsertLinePriceDecimals'),
            slotProps: {
              input: {
                max: PricingHelper.DEFAULT_MAX_DECIMALS,
                decimalScale: 0,
              },
            },
          }}
        />
      );
    case QuotePartUpdateBulkField.SHIPMENT_TYPE:
      return (
        <ShipmentTypeFormField
          name="shipmentType"
          label={null}
          requestPayload={{ filters: { deleted: 0 } }}
          listboxStyle={{ gap: 0 }}
        />
      );
    case QuotePartUpdateBulkField.SHIPPING_LOCATION:
      return (
        <CorporateLocationFormField
          name="corporateLocation"
          placeholder={t('placeholderUpsertLineCorporateLocation')}
          requestPayload={{
            filters: { vendorId: NON_EMPTY },
          }}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          type={CorporateLocationType.SHIPPING}
        />
      );
    case QuotePartUpdateBulkField.TAXONOMY:
      return <UpsertTaxonomyGroupFormFields withoutForm />;
    case QuotePartUpdateBulkField.MANUFACTURER:
      return (
        <ManufacturerFormField
          name="manufacturer"
          isAlternateVersionEnabled={isAlternateVersionEnabled}
        />
      );
    case QuotePartUpdateBulkField.PRIORITIZE_LEGACY_COST: {
      const prioritizeValue = watch('prioritizeLegacyCost');

      return (
        <FormField
          name="prioritizeLegacyCost"
          type={InputType.SWITCH}
          inputProps={{
            label: t(
              prioritizeValue ? 'messagePrioritizeLegacyCostOn' : 'messagePrioritizeLegacyCostOff'
            ),
            control,
            size: Size.MD,
            sx: { marginLeft: '30px' },
          }}
        />
      );
    }
    case QuotePartUpdateBulkField.PRICING_DATE:
      return <PricingDateFormField />;
    case QuotePartUpdateBulkField.PRICING_STRATEGY:
      return <PricingStrategyFormField />;
    default:
      return;
  }
};
